<template>
  <v-app-bar :color="colors.topHeader" elevation="1" density="compact">
    <v-row class="app-bar-left ma-0">
      <router-link to="/devices">
        <v-img
          id="insys-icom-logo"
          class="ml-4 mr-2"
          :src="$filters.assetUrl('assets/INSYS-icom-logo-white.svg')"
          height="24"
          width="56"
        />
      </router-link>
      <v-img :src="$filters.assetUrl('assets/iRM-text.svg')" height="16" />
    </v-row>

    <v-btn
      v-if="allowChangeAccount && notError"
      color="transparent"
      class="change-account-button text-none ml-3 mb-1"
      data-cy="logged-in-show-change-account"
      @click="openChangeAccountDialog"
    >
      <v-icon color="white">mdi-swap-horizontal</v-icon>
      <span class="user-name-and-organisation">
        {{ $t('app.changeAccount') }}
      </span>
    </v-btn>
    <v-spacer />
    <!-- <QuickActionMenu
      v-if="loggedIn && notError"
      :get-license-features="licensedFeatures"
      :onpremises="onpremises"
      :settings="settings"
    /> -->
    <NotificationBell v-if="!onPremises()" />
    <InfoLinksMenu />
    <AppBarMenu
      v-if="notError"
      :title="userMenuWithLogoutCallback.title"
      :icon="
        showLicenseAlert ? 'mdi-account-alert-outline' : 'mdi-account-outline'
      "
      :icon-color="showLicenseAlert ? 'orange' : 'white'"
      :items="userMenuWithLogoutCallback.items"
      :licensed-features="licensedFeatures"
      :is-admin-account="isAdminAccount"
    >
      <template #top>
        <v-row justify="end" class="ma-0">
          <LanguageSelector class="pt-3 px-1" />
        </v-row>
        <v-alert
          v-if="showLicenseAlert"
          type="warning"
          :text="getAlertText"
          class="px-3 py-2"
          data-cy="app-bar-license-expiry-alert"
        >
          <br />
          <v-btn
            variant="text"
            slim
            density="compact"
            prepend-icon="mdi-arrow-right"
            @click="navigateExpiringLicense"
          >
            {{
              isTrialAccount
                ? $t('upselling.upgradeButton')
                : $t('app.myLicenses')
            }}
          </v-btn>
        </v-alert>
      </template>
      <template #button>
        <span class="user-name-and-organisation">
          {{ loggedInUser.username }}
          <br />
          {{ loggedInUser?.organisation?.name }}
        </span>
      </template>
    </AppBarMenu>
    <ChangeAccountDialog
      v-if="showChangeAccountDialog"
      :show-dialog="showChangeAccountDialog"
      @update:show-dialog="showChangeAccountDialog = false"
    />
  </v-app-bar>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { colors } from '@/styles/colors';
import { mapState, mapActions } from 'pinia';
import { authenticationStore } from '@/store/pinia/AuthenticationStore';
import { userMenu } from './appBar/menus';
import { dateFormatter } from '@/util/dateFormatter';
import { licenseStore } from '@/store/pinia/LicenseStore';
import AuthApi from '@/services/auth/AuthApi';
import { onPremises } from '@/shared/utils';
import { initReleaseNotesModal } from '@/util/releaseNotes';
import { captureComparePlans } from '@/util/posthog';

export default defineComponent({
  name: 'AppBar',
  data: () => ({
    colors,
    onPremises,
    userMenu,
    showChangeAccountDialog: false,
    dateFormatter
  }),
  computed: {
    ...mapState(authenticationStore, [
      'loggedInUser',
      'allowChangeAccount',
      'licensedFeatures',
      'isAdminAccount',
      'isTrialAccount'
    ]),
    ...mapState(licenseStore, ['soonExpiringLicenses']),

    showLicenseAlert() {
      return onPremises() ? false : this.soonExpiringLicenses[0];
    },
    getAlertText(): string {
      return this.isTrialAccount
        ? this.$t('app.trialPeriod', {
            msg: this.leftDaysTilLicenseExpiration,
            msg2: dateFormatter.dateWithoutTime(
              this.soonExpiringLicenses[0].expiresAt
            )
          })
        : this.$t(
            'app.licenseExpiresSoon',
            {
              days: this.leftDaysTilLicenseExpiration,
              date: dateFormatter.dateWithoutTime(
                this.soonExpiringLicenses[0].expiresAt
              )
            },
            this.soonExpiringLicenses.length
          );
    },
    userMenuWithLogoutCallback() {
      return this.userMenu(this.doLogout.bind(this));
    },
    notError(): boolean {
      const routeName = this.$route.name as string;
      return !(routeName?.includes('500') || routeName?.includes('404'));
    },
    leftDaysTilLicenseExpiration(): number {
      const expires = new Date(this.soonExpiringLicenses[0].expiresAt);
      const now = new Date();
      return Math.floor(
        (expires.getTime() - now.getTime()) / (1000 * 3600 * 24)
      );
    }
  },
  mounted() {
    if (!onPremises()) {
      initReleaseNotesModal();
    }
  },
  created() {
    void this.fetchLicenses();
  },
  methods: {
    ...mapActions(licenseStore, ['fetchLicenses']),
    async doLogout() {
      await AuthApi.logout();
    },
    openChangeAccountDialog(): void {
      this.showChangeAccountDialog = true;
    },
    async navigateExpiringLicense(): Promise<void> {
      this.isTrialAccount
        ? await this.navigateToOverview()
        : await this.$router.push('/licenses');
    },
    async navigateToOverview(): Promise<void> {
      await this.$router.push('/plan-overview');
      captureComparePlans();
    }
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss' as colors;

.user-name-and-organisation {
  font-size: 13px;
  color: white !important;
  padding-left: 12px;
  padding-right: 12px;
  max-width: 320px;
  text-align: left !important;
  overflow: hidden !important;
}
.change-account-button:hover {
  background-color: colors.$darker-grey !important;
}
.app-bar-left {
  background-color: colors.$primary;
  height: 48px;
  max-width: 300px;
  min-width: 300px;
  align-items: center;
  clip-path: polygon(0 0, 100% 0, 91% 100%, 0% 100%);
}
a:hover {
  text-decoration: none;
}
</style>
